import React, { useState } from 'react'
import { Avatar, Box, Button, Rating, Typography, Stack } from '@mui/material'
import styled from 'styled-components'
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined'
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined'
import { changeFio, getFormFioShort } from '../../utils/changeFio'
import { getDateFormat } from '../../utils/getDateFormat'
import { parseLink } from '../../utils/parseLink'
import { NameTransfer, TypeMessage } from '../../utils/consts'
import { addParagraph } from '../../utils/addParagraph'
import { changeMessge } from '../../utils/changeMessage'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import { useAutoReplyTimer } from '../../hooks/useAutoReplyTimer'
import TimerIcon from '@mui/icons-material/Timer'
import { ConfirmModal } from '../modals/ConfirmModal'

// Base message interface with common fields
interface IMessageBase {
  absnum: number
  dialog_id?: number
  type_id: number
  expert_id?: number
  expert_fio?: string
  client_id?: number
  client_fio?: string
  photo?: string
  mailing_photo?: string
  sender?: string
  is_read?: number // Changed from boolean to number to match actual type
  is_edit?: number // Changed from boolean to number to match IMessageOnline
  adate?: Date // Changed from string to Date to match IMessageOnline and IMessageUnauthorized
  message?: string
  rating?: number
  body?: string
  img?: string
  link?: string
  btn_color?: string
  btn_name?: string
  passed_to_written?: number
  subscription?: 'BASIC' | 'STANDARD' | 'PRO'
  auto_reply?: {
    basic_timeout: number
    basic_message: string
    standard_timeout: number
    standard_message: string
    pro_timeout: number
    pro_message: string
  }
}

// Remove empty interfaces and use IMessageBase directly
type IMessageOnline = IMessageBase
type IMessageUnauthorized = IMessageBase

interface PropsMessage {
  message: IMessageBase
}

interface Props extends PropsMessage {
  isSelect?: boolean
  searchText?: string
  withTransferBtn?: boolean
  disableAutoReplyTimer?: boolean
  productColor?: string | null
  handleClick?: (event: React.MouseEvent<HTMLElement>) => void
  onCopyToWritten?: (message: string, message_id: number) => void
}

const MessageTransfer: React.FC<PropsMessage> = ({ message }) => {
  return (
    <div
      style={{
        width: '100%',
        textAlign: 'center',
        color: 'green',
        padding: '10px',
      }}
    >
      {NameTransfer[message.type_id]} чат. Консультант {message.expert_fio}
    </div>
  )
}

const MainWrapperS = styled(Box)``

const WrapperS = styled(Box)`
  border-radius: 4px;
  position: relative;
`

const WrapperMessageS = styled.div<{ isMy: boolean }>`
  position: relative;
  a {
    color: ${({ isMy }) => (isMy ? '#fff' : '')};
  }
  h4 {
    margin: 0 0 10px 0;
  }
  p:not([class]) {
    text-indent: 1.5em;
  }
`

const CopyButtonS = styled(Button)`
  margin-top: 8px !important;
  &.Mui-disabled {
    color: rgba(0, 0, 0, 0.26);
    background: transparent;
  }
`

export const ChatMessage: React.FC<Props> = ({ isSelect, message, searchText, handleClick, onCopyToWritten, disableAutoReplyTimer, withTransferBtn, productColor }) => {
  const [isMessageTransfered, setIsMessageTransfered] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

  const isMy = !!message.expert_id || (message.type_id > 0 && message.type_id !== TypeMessage.RATING)
  const styleObj = {
    bgcolor: isMy ? '#277091' : '#f2f4f8',
    textColor: isMy ? 'white' : '#5d5d5d',
    nameColor: isMy ? '#5d5d5d' : '#5d5d5d',
    avatarBg: isMy
      ? (message.photo ? 'initial' : 'orange')
      : (productColor || 'orange'),
    avatarTextColor: isMy ? 'white' : 'white',
  }

  const { bgcolor, textColor, avatarBg, avatarTextColor, nameColor } = styleObj

  const renderIcons = () => {
    if (message.is_read) {
      return <DoneAllOutlinedIcon color={'primary'} fontSize={'small'} sx={{ fontSize: 14 }} />
    }
    return <DoneOutlinedIcon fontSize={'small'} sx={{ fontSize: 14 }} />
  }

  const renderMesssage = () => {
    if (message.type_id === TypeMessage.RATING)
      return <Rating name='read-only' value={message.rating} readOnly size={'small'} />
    if (message.type_id === TypeMessage.MAILING)
      return (
        <Box px={1} py={1} bgcolor={bgcolor}>
          <Typography
            pb={1}
            fontSize={13}
            color={textColor}
            style={{ wordBreak: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: changeMessge(addParagraph(message.body), searchText) }}
          />
          {message.img ? (
            <img
              style={{ maxHeight: '450px', maxWidth: '290px', width: '100%', display: 'block', marginBottom: '10px' }}
              src={message.img}
              alt='Зображення'
            />
          ) : null}
          <Button
            sx={{ mt: 2, display: 'inline' }}
            fullWidth
            variant='contained'
            href={message.link}
            style={{ backgroundColor: message.btn_color, whiteSpace: 'nowrap' }}
          >
            {message.btn_name}
          </Button>
        </Box>
      )
    return (
      <Typography
        bgcolor={bgcolor}
        px={1}
        py={1}
        fontSize={13}
        color={textColor}
        style={{ wordBreak: 'break-word' }}
        dangerouslySetInnerHTML={{
          __html:
            message.type_id === TypeMessage.AUTO
              ? `<h4>Автоповідомлення</h4> ${changeMessge(parseLink(message.message), searchText)}`
              : changeMessge(parseLink(message.message), searchText),
        }}
      />
    )
  }

  const handleCopyToWritten = () => {
    if (onCopyToWritten && message.message) {
      try {
        setIsMessageTransfered(true)
        onCopyToWritten(message.message, message.absnum)
      } catch {
        setIsMessageTransfered(false)
      }
    }
  }

  const handleOpenConfirmModal = () => setIsConfirmModalOpen(true)
  const handleCloseConfirmModal = () => setIsConfirmModalOpen(false)
  const handleConfirmCopyToWritten = () => {
    handleCloseConfirmModal()
    handleCopyToWritten()
  }

  const { timeLeft } = useAutoReplyTimer({
    autoReplyConfig: message.auto_reply,
    subscription: message.subscription || 'BASIC', // Default to BASIC if not specified
    onTimeout: (autoReplyMessage: string) => {
      // This function is not directly passed to the component as a prop,
      // so it will not be called here.
      // If you need to update the message state or trigger an action,
      // you would typically do it here or in a parent component.
      console.log('Auto-reply timeout:', autoReplyMessage)
    }
  })

  return (
    <MainWrapperS
      display={'flex'}
      justifyContent={isMy ? 'end' : ' start'}
      sx={{ background: isSelect ? '#fff3e0' : '' }}
      onContextMenu={handleClick}
    >
      {message.type_id === TypeMessage.TRANSFERED || message.type_id === TypeMessage.RECIVED ? (
        <MessageTransfer message={message} />
      ) : (
        <WrapperS sx={{ px: 1, py: 1, mb: 2, borderRadius: 1 }} display='flex' maxWidth={'90%'}>
          <Avatar
            src={message.photo || message.mailing_photo}
            sx={{ bgcolor: avatarBg, mr: 1, mt: 1, color: avatarTextColor }}
          >
            {isMy ? getFormFioShort(message.expert_fio) : getFormFioShort(message.client_fio)}
          </Avatar>
          <Box>
            <Box display={'flex'} alignItems={'flex-start'} flexDirection={'column'}>
              <Typography component='h6' fontWeight={500} variant={'h6'} color={nameColor} fontSize={13}>
                {isMy ? message.sender || changeFio(message.expert_fio) : message.client_id ? changeFio(message.client_fio) : 'Неавторизований'}
              </Typography>
            </Box>

            <WrapperMessageS isMy={isMy}>
              {renderMesssage()}
              {!isMy && onCopyToWritten && message.type_id === 0 && message.client_id && withTransferBtn && (
                <CopyButtonS
                  size="small"
                  onClick={handleOpenConfirmModal}
                  startIcon={<FileCopyIcon />}
                  disabled={!!message.passed_to_written || isMessageTransfered}
                >
                  {!!message.passed_to_written ? 'Передано' : 'Передати в письмові'}
                </CopyButtonS>
              )}
              <ConfirmModal
                open={isConfirmModalOpen}
                closeHandler={handleCloseConfirmModal}
                title='Ви впевнені, що хочете передати це повідомлення в письмові?'
                btnNameConfirm='Так, передати'
                btnNameCancel='Скасувати'
                confirmColor='success'
                delHandler={(callback) => {
                  try {
                    handleConfirmCopyToWritten()
                  } catch {
                    callback('Не вдалося передати повідомлення. Спробуйте ще раз.')
                  }
                }}
              />
            </WrapperMessageS>

            <Box mt={1} width={'100%'} display={'flex'} justifyContent={'flex-end'} alignItems={'center'}>
              {isMy && renderIcons()}
              <Typography ml={0.5} component={'small'} color={nameColor} fontSize={10}>
                {getDateFormat(message.adate ? new Date(message.adate) : undefined)} {message.is_edit ? 'Відредаговано' : ''}
              </Typography>
            </Box>

            {timeLeft && !disableAutoReplyTimer && message.type_id === 0 && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <TimerIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Час до автовідповіді: {timeLeft}
                </Typography>
              </Stack>
            )}
          </Box>
        </WrapperS>
      )}
    </MainWrapperS>
  )
}
